// Top toolbar: title, resolution, undo/redo, share, export, theme.

import { effect } from '@/core/signals';
import {
  state,
  canUndo,
  canRedo,
  undo,
  redo,
  setResolution,
  toggleTheme,
  theme,
} from '@/core/state';
import { shareableUrl, shareableDsl } from '@/core/url';
import { el, on } from '@/lib/dom';
import { ICON } from './icons';
import type { ViewportApi } from './viewport';
import { openPresetGallery } from './presetGallery';
import { openDslEditor } from './dslEditor';
import { openExportDialog } from './exportDialog';
import { surpriseMe } from '@/lib/surprise';
import { openVariations } from './variations';
import { toggleCheatsheet } from './cheatsheet';
import { openEffectsBrowser } from './effectsBrowser';
import { toggleFullscreen, toggleZen, isFullscreen } from '@/lib/fullscreen';
import { toggleStudio, isStudio } from '@/lib/studioMode';

const RES_PRESETS: { label: string; w: number; h: number }[] = [
  { label: '32',   w: 32,  h: 32  },
  { label: '64',   w: 64,  h: 64  },
  { label: '128',  w: 128, h: 128 },
  { label: '256',  w: 256, h: 256 },
  { label: '384',  w: 384, h: 384 },
  { label: '512',  w: 512, h: 512 },
  { label: '1024', w: 1024, h: 1024 },
];

export function mountToolbar(host: HTMLElement, viewport: ViewportApi): () => void {
  // Brand
  const brand = el('div', { class: 'tb-brand' }, [
    el('span', { class: 'tb-mark' }, ['◾◾◾']),
    el('strong', { class: 'tb-name' }, ['PIXELLAB']),
  ]);

  // Resolution selector
  const resSel = el('select', { class: 'tb-res', ariaLabel: 'Resolution' });
  for (const r of RES_PRESETS) {
    const o = el('option', { value: `${r.w}x${r.h}` }, [`${r.label}`]);
    resSel.appendChild(o);
  }
  on(resSel, 'change', () => {
    const [w, h] = (resSel as HTMLSelectElement).value.split('x').map(Number);
    setResolution({ w: w as number, h: h as number });
  });

  // Zoom buttons
  const zoomOut = btn(ICON.zoomOut, 'Zoom out', () => viewport.zoomOut());
  const zoomFit = el('button', { class: 'btn tb-zoom-label', ariaLabel: 'Fit' }, ['fit']);
  on(zoomFit, 'click', () => viewport.fit());
  const zoomIn = btn(ICON.zoomIn, 'Zoom in', () => viewport.zoomIn());

  // Undo / redo
  const btnUndo = btn(ICON.undo, 'Undo (⌘Z)', () => undo());
  const btnRedo = btn(ICON.redo, 'Redo (⌘⇧Z)', () => redo());

  // Share
  const btnShare = btn(ICON.share, 'Copy share URL', async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl());
      flash(btnShare);
    } catch {
      // Some browsers require https + user gesture; ignore.
    }
  });

  // Open DSL editor
  const btnDsl = btn(ICON.code, 'DSL editor', () => openDslEditor());

  // Presets
  const btnPresets = btn(ICON.layer, 'Presets', () => openPresetGallery());

  // Surprise me
  const btnSurprise = btn(ICON.shuffle, 'Surprise me (X)', () => surpriseMe());

  // Variations of current stack
  const btnVariations = btn(ICON.copy, 'Variations of current stack', () => openVariations());

  // Effects browser — walks 5,480 unique compositions
  const btnBrowse = btn(BROWSE_ICON, 'Browse 5,480 effects (B)', () => openEffectsBrowser());

  // Studio mode (the iamnoman-inspired focused dot-gradient experience)
  const btnStudio = btn(STUDIO_ICON, 'Studio mode (M)', () => toggleStudio());

  // Fullscreen + zen
  const btnFull = btn(FULL_ICON, 'Fullscreen (F)', () => toggleFullscreen());
  const btnZen = btn(ZEN_ICON, 'Zen mode — hide UI (Z)', () => toggleZen());

  // Help / cheatsheet
  const btnHelp = btn(ICON.code, 'Keyboard cheatsheet (?)', () => toggleCheatsheet());
  btnHelp.innerHTML = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="8" cy="8" r="6"/><path d="M6.3 6.2c.2-1.5 3.4-1.7 3.4 0 0 1.2-1.7 1.4-1.7 2.6"/><circle cx="8" cy="11.5" r=".7" fill="currentColor"/></svg>';

  // Export menu
  const btnExport = btn(ICON.download, 'Export…', () => openExportDialog());

  // Theme toggle
  const btnTheme = btn(ICON.sun, 'Toggle theme', toggleTheme);

  // Layout
  const left = el('div', { class: 'tb-group' }, [brand]);
  const center = el('div', { class: 'tb-group tb-center' }, [
    label('Size'), resSel,
    sep(),
    zoomOut, zoomFit, zoomIn,
    sep(),
    btnUndo, btnRedo,
  ]);
  const right = el('div', { class: 'tb-group tb-right' }, [
    btnStudio, btnPresets, btnSurprise, btnVariations, btnBrowse, sep(), btnDsl, btnShare, btnExport, sep(), btnZen, btnFull, btnHelp, btnTheme,
  ]);

  host.append(left, center, right);

  // Reactive: undo/redo enable, resolution sync, theme icon, fullscreen icon
  const dispose = effect(() => {
    btnUndo.toggleAttribute('disabled', !canUndo.get());
    btnRedo.toggleAttribute('disabled', !canRedo.get());
    const s = state.get();
    const value = `${s.resolution.w}x${s.resolution.h}`;
    if ((resSel as HTMLSelectElement).value !== value) {
      // Add the value as an option if not in presets
      if (!RES_PRESETS.some((r) => `${r.w}x${r.h}` === value)) {
        const o = el('option', { value }, [`${s.resolution.w}×${s.resolution.h}`]);
        resSel.appendChild(o);
      }
      (resSel as HTMLSelectElement).value = value;
    }
    btnTheme.innerHTML = theme.get() === 'dark' ? ICON.sun : ICON.moon;
    btnFull.innerHTML = isFullscreen.get() ? FULL_EXIT_ICON : FULL_ICON;
    btnStudio.classList.toggle('on', isStudio.get());
  });

  return dispose;
}

function btn(icon: string, label: string, click: () => void): HTMLButtonElement {
  const b = el('button', { class: 'btn-icon', ariaLabel: label, title: label, innerHTML: icon });
  on(b, 'click', click);
  return b as HTMLButtonElement;
}

function sep(): HTMLElement {
  return el('span', { class: 'tb-sep' });
}

function label(text: string): HTMLElement {
  return el('span', { class: 'tb-label' }, [text]);
}

function flash(b: HTMLElement): void {
  b.classList.add('flash');
  setTimeout(() => b.classList.remove('flash'), 600);
}

// shareableDsl available for command-palette future feature
void shareableDsl;

const BROWSE_ICON = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="2" width="5" height="5"/><rect x="9" y="2" width="5" height="5"/><rect x="2" y="9" width="5" height="5"/><rect x="9" y="9" width="5" height="5"/></svg>';
const FULL_ICON   = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4"/></svg>';
const FULL_EXIT_ICON = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4"/></svg>';
const ZEN_ICON    = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true"><circle cx="8" cy="8" r="3"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2"/></svg>';
const STUDIO_ICON = '<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" stroke="none" aria-hidden="true"><circle cx="3" cy="3" r="1.4"/><circle cx="8" cy="3" r="1.4"/><circle cx="13" cy="3" r="1.4"/><circle cx="3" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/><circle cx="13" cy="8" r="1.4"/><circle cx="3" cy="13" r="1.4"/><circle cx="8" cy="13" r="1.4"/><circle cx="13" cy="13" r="1.4"/></svg>';
